/**
 * Simple router for handling different application routes
 */

import { UILanguage, DEFAULT_UI_LANGUAGE } from '@/i18n/types';
import { getTranslations, getTranslationValue } from '@/i18n';

export enum AppRoute {
  HOME = 'home',
  CHATS = 'chats',
  SETTINGS = 'settings',
  LOGIN = 'login',
  PURCHASE = 'purchase',
  ORDER_HISTORY = 'order-history',
  AUTH_CALLBACK = 'auth-callback',
  ADMIN = 'admin'
}

export interface RouteInfo {
  path: string;
  titleKey: string; // Translation key instead of hardcoded title
}

// Route configuration with translation keys
export const ROUTES: Record<AppRoute, RouteInfo> = {
  [AppRoute.HOME]: { path: '/', titleKey: 'nav.translation' },
  [AppRoute.CHATS]: { path: '/chats', titleKey: 'nav.history' },
  [AppRoute.SETTINGS]: { path: '/settings', titleKey: 'nav.settings' },
  [AppRoute.LOGIN]: { path: '/login', titleKey: 'auth.login' },
  [AppRoute.PURCHASE]: { path: '/purchase', titleKey: 'purchase.title' },
  [AppRoute.ORDER_HISTORY]: { path: '/order-history', titleKey: 'purchase.orderHistory' },
  [AppRoute.AUTH_CALLBACK]: { path: '/auth/callback', titleKey: 'auth.callback' },
  [AppRoute.ADMIN]: { path: '/admin', titleKey: 'admin.title' }
};

/**
 * Get localized route title
 */
export function getRouteTitle(route: AppRoute, language: UILanguage): string {
  const routeInfo = ROUTES[route];
  if (!routeInfo) return route;

  const translations = getTranslations(language);
  return getTranslationValue(translations, routeInfo.titleKey, routeInfo.titleKey);
}

export class SimpleRouter {
  private currentRoute: AppRoute = AppRoute.HOME;
  private listeners: ((route: AppRoute) => void)[] = [];
  private currentLanguage: UILanguage = DEFAULT_UI_LANGUAGE;

  constructor() {
    // 监听浏览器前进后退
    window.addEventListener('popstate', this.handlePopState.bind(this));
    // 初始化路由
    this.updateFromURL();
  }

  private handlePopState = () => {
    this.updateFromURL();
  };

  private updateFromURL() {
    const path = window.location.pathname;
    const route = this.pathToRoute(path);
    this.setCurrentRoute(route, false); // false 表示不更新 URL
  }

  private pathToRoute(path: string): AppRoute {
    for (const [route, info] of Object.entries(ROUTES)) {
      if (info.path === path || (path === '/' && route === AppRoute.HOME)) {
        return route as AppRoute;
      }
    }
    return AppRoute.HOME; // 默认路由
  }

  private setCurrentRoute(route: AppRoute, updateURL: boolean = true) {
    if (this.currentRoute !== route) {
      this.currentRoute = route;

      if (updateURL) {
        const routeInfo = ROUTES[route];
        const routeTitle = getRouteTitle(route, this.currentLanguage);
        window.history.pushState({}, routeTitle, routeInfo.path);

        // Update document title with localized text
        const appName = this.getLocalizedAppName();
        document.title = `${appName} - ${routeTitle}`;
      }

      // 通知所有监听器
      this.listeners.forEach(listener => listener(route));
    }
  }

  public navigate(route: AppRoute) {
    this.setCurrentRoute(route, true);
  }

  public getCurrentRoute(): AppRoute {
    return this.currentRoute;
  }

  public subscribe(listener: (route: AppRoute) => void) {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Update the current language for route titles
   */
  public setLanguage(language: UILanguage) {
    this.currentLanguage = language;
    // Update document title with new language
    this.updateDocumentTitle();
  }

  /**
   * Get localized route title
   */
  public getRouteTitle(route: AppRoute): string {
    return getRouteTitle(route, this.currentLanguage);
  }

  /**
   * Get localized app name
   */
  private getLocalizedAppName(): string {
    const translations = getTranslations(this.currentLanguage);
    return getTranslationValue(translations, 'home.title', '闪译猫');
  }

  /**
   * Update document title with current route and language
   */
  private updateDocumentTitle() {
    const routeTitle = this.getRouteTitle(this.currentRoute);
    const appName = this.getLocalizedAppName();
    document.title = `${appName} - ${routeTitle}`;
  }
}

// 全局路由实例
export const router = new SimpleRouter(); 